import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef, useMemo, useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { InputCheckbox } from "@creditornot/cb-checkbox";
import { typographyCss, colors, cbSemanticCssVars, elevationCss } from "@creditornot/cb-ingredients";
import { Empty } from "@creditornot/cb-placeholders";
import { Spinner } from "@creditornot/cb-progress";
import { createThemedCssVarDefinitions, configureCssVars, pxToRem, createCssVars } from "@creditornot/cb-utils";
import { Tooltip, Popover } from "@creditornot/cb-popover";
import { TextButton } from "@creditornot/cb-button";
import { MenuActionItem } from "@creditornot/cb-menu";
const SvgCaretdown = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12.028 19.5c.662 0 1.292-.283 1.73-.778l9.81-11.13a1.84 1.84 0 00-.195-2.568 1.85 1.85 0 00-2.578.134l-8.579 9.732a.25.25 0 01-.376 0L3.262 5.158a1.849 1.849 0 00-3.17.609A1.84 1.84 0 00.49 7.592l9.807 11.126a2.312 2.312 0 001.732.782z" }) }));
const CaretDownIcon = SvgCaretdown;
const SvgCaretleft = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M4.5 11.972c0-.662.283-1.292.778-1.73l11.13-9.81a1.84 1.84 0 012.568.195 1.85 1.85 0 01-.134 2.578L9.11 11.784a.25.25 0 000 .376l9.732 8.578a1.849 1.849 0 01-.609 3.17 1.84 1.84 0 01-1.825-.397L5.282 13.704a2.312 2.312 0 01-.782-1.732z" }) }));
const CaretLeftIcon = SvgCaretleft;
const SvgCaretright = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M19.5 11.972c0-.662-.283-1.292-.778-1.73L7.592.433a1.84 1.84 0 00-2.568.195 1.85 1.85 0 00.134 2.578l9.732 8.579a.25.25 0 010 .376l-9.732 8.578a1.849 1.849 0 00.609 3.17 1.84 1.84 0 001.825-.397l11.126-9.807a2.312 2.312 0 00.782-1.732z" }) }));
const CaretRightIcon = SvgCaretright;
const InfoCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.993 5.376 18.624.007 12 0zm.25 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2.25 13.5h-4a1 1 0 010-2h.75a.25.25 0 00.25-.25v-4.5a.25.25 0 00-.25-.25h-.75a1 1 0 010-2h1a2 2 0 012 2v4.75c0 .138.112.25.25.25h.75a1 1 0 010 2z" }) }));
const InfoCircleFilledIcon = InfoCircleFilled;
const Root$2 = styled.th`
  /* resets */
  padding-top: 0;
  padding-bottom: 0;
  box-sizing: border-box;

  position: relative;
  z-index: 0;

  ${typographyCss.Subtitle()}

  ${({ $clickable }) => $clickable && css`
      cursor: pointer;
      &:active {
        ${Content$1} {
          opacity: 0.8;
        }
      }

      &:hover {
        ${InvisibleCaretIcon} {
          opacity: 1;
        }
      }
    `}

  :focus:not(:focus-visible) {
    outline: none;
  }
  :focus-visible {
    outline: none;
    background-color: ${colors.wolt8};
  }
`;
const contentAlignmentMap = {
  left: "flex-start",
  center: "center",
  right: "flex-end"
};
const textAlignmentMap = {
  left: "start",
  center: "center",
  right: "end"
};
const CaretIcon = styled(CaretDownIcon)`
  ${createThemedCssVarDefinitions({
  fill: { light: colors.pepper64, dark: colors.salt72 }
})}
  flex-shrink: 0;
  fill: var(--fill);
  margin-inline-end: 4px;
  width: 8px;
  transition: transform 100ms ease-in;
  transform: scaleY(${({ $direction }) => $direction === "up" ? -1 : 1});
`;
const InvisibleCaretIcon = styled(CaretIcon)`
  opacity: 0;
  transition: opacity 100ms ease-in;
`;
const Content$1 = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => contentAlignmentMap[$align]};
  text-align: ${({ $align }) => textAlignmentMap[$align]};
`;
const Name = styled.div`
  margin-inline-end: 4px;
`;
const InfoIcon = styled(InfoCircleFilledIcon)`
  flex-shrink: 0;
  width: 12px;
  fill: ${cbSemanticCssVars.textSecondary};
  margin-inline-end: 4px;
`;
const HeaderColumn = ({
  align,
  children,
  info,
  onKeyDown,
  onSortChange,
  sortDirection,
  sortDirections = [],
  ...rest
}) => {
  const clickable = sortDirections.length > 0;
  const nextSortDirection = useMemo(() => {
    if (sortDirection === "descend" && sortDirections.includes("ascend")) {
      return "ascend";
    } else if (sortDirection === "ascend" && sortDirections.includes("descend")) {
      return "descend";
    } else if (sortDirections.includes("ascend")) {
      return "ascend";
    } else if (sortDirections.includes("descend")) {
      return "descend";
    }
    return sortDirection;
  }, [sortDirection, sortDirections]);
  const handleClick = useCallback(() => {
    if (onSortChange && nextSortDirection) {
      onSortChange(nextSortDirection);
    }
  }, [nextSortDirection, onSortChange]);
  const handleKeyDown = useCallback(
    (event) => {
      if (onSortChange && nextSortDirection && (event.key === " " || event.key === "Enter")) {
        onSortChange(nextSortDirection);
      }
      onKeyDown == null ? void 0 : onKeyDown(event);
    },
    [nextSortDirection, onSortChange, onKeyDown]
  );
  return /* @__PURE__ */ jsx(
    Root$2,
    {
      ...rest,
      $clickable: clickable,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      tabIndex: clickable ? 0 : void 0,
      children: /* @__PURE__ */ jsxs(Content$1, { $align: align, children: [
        align === "left" && children && /* @__PURE__ */ jsx(Name, { children }),
        !!sortDirection && /* @__PURE__ */ jsx(CaretIcon, { $direction: sortDirection === "ascend" ? "up" : "down" }),
        !sortDirection && sortDirections.length > 0 && /* @__PURE__ */ jsx(InvisibleCaretIcon, { $direction: sortDirections.includes("ascend") ? "up" : "down" }),
        align === "right" && children && /* @__PURE__ */ jsx(Name, { children }),
        !!info && /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(Tooltip, { content: info, children: /* @__PURE__ */ jsx(InfoIcon, {}) }) })
      ] })
    }
  );
};
const noop = () => {
};
const PopoverContent = styled.div`
  border-radius: 8px;
  overflow: hidden;
  max-height: 320px;
  min-width: 72px;
  padding: 8px;
`;
const PageSizesList = styled.div`
  overflow-y: auto;
  max-height: 320px;
`;
const StyledMenuActionItem = styled(MenuActionItem)`
  & + & {
    margin-top: 8px;
  }
`;
const PageSizeSelector = ({ className, onChange = noop, options, value }) => /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(
  Popover,
  {
    content: ({ close }) => /* @__PURE__ */ jsx(PopoverContent, { children: /* @__PURE__ */ jsx(PageSizesList, { children: options.map((option) => /* @__PURE__ */ jsx(
      StyledMenuActionItem,
      {
        onClick: () => {
          close();
          onChange(option);
        },
        selected: option === value,
        variant: "black",
        children: option
      },
      option
    )) }) }),
    fallbackPlacements: ["top-end"],
    placement: "bottom-end",
    children: ({ refProps }) => /* @__PURE__ */ jsxs(TextButton, { ...refProps, icon: /* @__PURE__ */ jsx(CaretDownIcon, {}), iconPosition: "right", children: [
      "Show ",
      value
    ] })
  }
) });
const sortNumber = (a, b) => a - b;
const unique = (arr) => [...new Set(arr)];
const last = (arr) => arr.length > 0 ? arr.at(-1) : void 0;
const isNumberArr = (value) => Array.isArray(value) && (value.length === 0 || typeof value[0] === "number");
const createPageSets = ({
  activePage,
  around = 2,
  pages
}) => {
  const numbers = [...Array(pages).keys()].map((p) => p + 1);
  const activeIndex = numbers.indexOf(activePage);
  const firstPage = numbers[0];
  const lastPage = numbers.at(-1);
  const aroundStartIndex = activeIndex - around <= 0 ? 0 : activeIndex - around;
  const aroundEndIndex = activeIndex + 1 + around >= numbers.length ? numbers.length : activeIndex + 1 + around;
  const startSlice = numbers.slice(0, 1 + 2 * around);
  const aroundSlice = numbers.slice(aroundStartIndex, aroundEndIndex);
  const endSlice = numbers.slice(pages - 1 - 2 * around);
  const middleSlice = unique(
    [
      startSlice.includes(activePage) && startSlice,
      endSlice.includes(activePage) && endSlice,
      aroundSlice
    ].filter((value) => value !== false).flat(1)
  ).sort(sortNumber);
  return [
    !middleSlice.includes(firstPage) && [firstPage],
    middleSlice,
    lastPage !== void 0 && !middleSlice.includes(lastPage) && [lastPage]
  ].filter(isNumberArr).filter((set) => !!set.length);
};
const { cssString, cssVars: colorCssVars$1 } = configureCssVars({
  borderActive: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  textActive: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  dot: {
    light: colors.pepper24,
    lightHighContrast: colors.pepper40,
    dark: colors.salt28
  }
});
const PaginationButton = styled.button`
  ${typographyCss.baseFontFamily()}
  background: transparent;
  font-weight: 500;
  font-size: ${pxToRem(13)};
  line-height: ${pxToRem(14)};
  border: 1px solid transparent;
  border-radius: 4px;

  display: flex;
  min-width: 28px;
  height: 28px;
  padding: 2px 4px;

  align-items: center;
  justify-content: center;

  color: ${cbSemanticCssVars.textSecondary};
  cursor: pointer;
  transition: color 50ms ease-in;

  &:disabled {
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    border-color: ${colorCssVars$1.borderActive};
    color: ${colorCssVars$1.textActive};
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
  :focus-visible {
    outline: 2px transparent solid;
    box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
  }

  &[aria-current] {
    border-color: ${colorCssVars$1.borderActive};
    color: ${colorCssVars$1.textActive};
  }

  & + & {
    margin-inline-start: 4px;
  }

  svg {
    fill: ${cbSemanticCssVars.textSecondary};
  }

  &:not(:disabled):hover svg {
    fill: currentColor;
  }
`;
const CaretLeft = styled(CaretLeftIcon)``;
const PrevButton = styled(PaginationButton)`
  margin-inline-end: 4px;

  [dir='rtl'] & ${CaretLeft} {
    transform: scaleX(-1);
  }
`;
const CaretRight = styled(CaretRightIcon)``;
const NextButton = styled(PaginationButton)`
  margin-inline-start: 4px;

  [dir='rtl'] & ${CaretRight} {
    transform: scaleX(-1);
  }
`;
const NumberSet = styled.div`
  display: flex;
  align-items: center;
`;
const Root$1 = styled.div`
  ${cssString}
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Dot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 1.5px;
  background-color: ${colorCssVars$1.dot};

  & + & {
    margin-inline-start: 2px;
  }
`;
const ThreeDotsRoot = styled.div`
  margin-inline-start: 12px;
  margin-inline-end: 12px;
  display: flex;
  align-items: center;
`;
const ThreeDots = () => /* @__PURE__ */ jsxs(ThreeDotsRoot, { children: [
  /* @__PURE__ */ jsx(Dot, {}),
  /* @__PURE__ */ jsx(Dot, {}),
  /* @__PURE__ */ jsx(Dot, {})
] });
const Pagination = ({
  activePage,
  "aria-label": ariaLabel = "Pagination",
  disabled,
  onPageChange = () => {
  },
  pages,
  showPrevNext = true,
  ...rest
}) => {
  const onPreviousPageClick = () => onPageChange(activePage - 1);
  const onNextPageClick = () => onPageChange(activePage + 1);
  const pageNumberSets = useMemo(() => createPageSets({ activePage, pages }), [activePage, pages]);
  return /* @__PURE__ */ jsxs(Root$1, { role: "navigation", ...rest, "aria-label": ariaLabel, children: [
    !!showPrevNext && /* @__PURE__ */ jsx(
      PrevButton,
      {
        disabled: disabled || activePage === 1,
        onClick: onPreviousPageClick,
        title: "Previous page",
        type: "button",
        children: /* @__PURE__ */ jsx(CaretLeft, { height: "10px" })
      }
    ),
    pageNumberSets.map((numberSet, i) => {
      const isLastNumberSet = i === pageNumberSets.length - 1;
      const elem = /* @__PURE__ */ jsx(NumberSet, { children: numberSet.map((page) => /* @__PURE__ */ jsx(
        PaginationButton,
        {
          "aria-current": activePage === page ? "page" : void 0,
          disabled,
          onClick: () => {
            if (onPageChange) {
              onPageChange(page);
            }
          },
          title: `Page ${page} of ${pages}`,
          type: "button",
          children: page
        },
        page
      )) }, i);
      return isLastNumberSet ? [elem] : [elem, /* @__PURE__ */ jsx(ThreeDots, {}, `${i}n`)];
    }),
    !!showPrevNext && /* @__PURE__ */ jsx(
      NextButton,
      {
        disabled: disabled || activePage === last(last(pageNumberSets) || []),
        onClick: onNextPageClick,
        title: "Next page",
        type: "button",
        children: /* @__PURE__ */ jsx(CaretRight, { height: "10px" })
      }
    )
  ] });
};
const DEFAULT_PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const horizontalPaddingConfig = {
  none: 0,
  narrow: 16,
  wide: 24
};
const logicalTextAlign = {
  left: "start",
  center: "center",
  right: "end"
};
const colorCssVars = createCssVars([
  "backgroundColor",
  "borderColor",
  "clickableRowBackgroundHoveredUnselected"
]);
const Root = styled.div`
  ${createThemedCssVarDefinitions({
  backgroundColor: {
    light: colors.salt100,
    dark: "transparent"
  },
  borderColor: {
    // no alpha on border color to prevent border overlap bugs
    light: "#EEEEEE",
    // on dark mode there has to be alpha since the background may change due to elevation
    dark: colors.salt12
  },
  clickableRowBackgroundHoveredUnselected: {
    light: colors.pepper8,
    dark: colors.salt12
  }
})}

  box-sizing: border-box;
  background-color: ${colorCssVars.backgroundColor};
  color: ${cbSemanticCssVars.textPrimary};
  border-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
  ${({ $elevationLevel }) => !!$elevationLevel && elevationCss($elevationLevel)}
`;
const Header = styled.header`
  box-sizing: border-box;
  min-height: 80px;
  padding: 12px ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;
  border-bottom: 1px solid ${colorCssVars.borderColor};
  border-top-left-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
  border-top-right-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
  display: flex;
  align-items: center;
`;
const Content = styled.div`
  z-index: 0;
  position: relative;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-inline-start: ${({ $horizontalPadding, $verticalColumnBorders }) => $verticalColumnBorders ? 0 : horizontalPaddingConfig[$horizontalPadding] / 2}px;
`;
const EmptyContainer = styled.div`
  padding: 48px 8px;
`;
const EmptySectionContentColumn = styled.td`
  border-top: 1px solid ${colorCssVars.borderColor};
`;
const SpinnerContainer = styled.div`
  padding: 48px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SpinnerOverlayContainer = styled.div`
  position: absolute;
  top: calc(${({ $offset }) => $offset}px + 1px);
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  display: flex;
  background-color: ${cbSemanticCssVars.surfaceMain};
  align-items: center;
  justify-content: center;
  border-bottom-left-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
  border-bottom-right-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
  ${({ $elevationLevel }) => elevationCss($elevationLevel)}
  box-shadow: none;
`;
const Table = styled.table.attrs({ cellPadding: 0, cellSpacing: 0 })`
  /* resets */
  border-spacing: 0;
  border-collapse: collapse;

  width: 100%;
  position: relative;
  border-radius: ${({ $rounded }) => $rounded ? "8px" : 0};

  table-layout: ${({ $tableLayout }) => $tableLayout};
`;
const TableHeader = styled.thead`
  text-align: start;

  padding-inline-start: 8px;
  padding-inline-end: 8px;
  z-index: 2;
`;
const HeaderRow = styled.tr`
  > th {
    border-bottom: 1px solid ${colorCssVars.borderColor};
    height: ${({ $verticalSpacing }) => $verticalSpacing === "spacious" ? 56 : 36}px;

    padding-top: 8px;
    padding-bottom: 8px;
    padding-inline-start: ${({ $verticalColumnBorders }) => $verticalColumnBorders ? 16 : 8}px;
    padding-inline-end: ${({ $verticalColumnBorders }) => $verticalColumnBorders ? 16 : 8}px;
  }

  > th + th {
    border-inline-start: ${({ $verticalColumnBorders }) => $verticalColumnBorders ? "1px" : 0}
      solid ${colorCssVars.borderColor};
  }

  > th:first-child {
    border-top-left-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
    padding-inline-start: ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;
  }
  > th:last-child {
    border-top-right-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
    padding-inline-end: ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;
  }
`;
const HeaderSelectionColumn = styled(HeaderColumn)`
  width: 40px;
`;
const Body = styled.tbody`
  z-index: 1;

  > tr {
    height: ${({ $verticalSpacing }) => $verticalSpacing === "spacious" ? 56 : 36}px;
  }

  > tr td {
    scroll-snap-align: start;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  > tr + tr {
    border-top: 1px solid ${colorCssVars.borderColor};
  }

  > tr td:first-child {
    padding-inline-start: ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;
  }

  > tr td:last-child {
    padding-inline-end: ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;
  }

  > tr:last-child {
    border-bottom-right-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
    border-bottom-left-radius: ${({ $rounded }) => $rounded ? "8px" : 0};

    td:first-child {
      border-bottom-left-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
    }
    td:last-child {
      border-bottom-right-radius: ${({ $rounded }) => $rounded ? "8px" : 0};
    }
  }
`;
const Row = styled.tr`
  > td {
    padding-inline-start: ${({ $verticalColumnBorders }) => $verticalColumnBorders ? 16 : 8}px;
    padding-inline-end: ${({ $verticalColumnBorders }) => $verticalColumnBorders ? 16 : 8}px;
  }

  > td + td {
    border-inline-start: ${({ $verticalColumnBorders }) => $verticalColumnBorders ? "1px" : 0}
      solid ${colorCssVars.borderColor};
  }

  ${({ $clickDisabled, $hasClickAssigned }) => $hasClickAssigned && $clickDisabled && css`
      cursor: not-allowed;
    `}

  &:focus {
    outline: none;
    background: ${colors.wolt8};
  }

  ${({ $active, $clickDisabled, $hasClickAssigned }) => $hasClickAssigned && !$clickDisabled && css`
      cursor: pointer;

      &:hover {
        background: ${colors.wolt8};
      }

      &:active {
        background: ${colors.wolt12};
      }

      &:focus {
        ${$active && `background: ${colors.wolt12}`}
      }
    `}
  ${({ $highlightSelectedRow, $selected }) => $highlightSelectedRow && $selected && css`
      background: ${colors.wolt8};

      &:hover {
        background: ${colors.wolt12};
      }
    `}

  ${({ $hasClickAssigned, $highlightSelectedRow, $highlightUnclickableRowOnHover, $selected }) => !$hasClickAssigned && $highlightSelectedRow && $highlightUnclickableRowOnHover && css`
      background: ${$selected ? colors.wolt8 : "inherit"};

      &:hover {
        background: ${$selected ? colors.wolt12 : colorCssVars.clickableRowBackgroundHoveredUnselected};
      }
    `}
`;
const SectionTitle = styled.div`
  ${typographyCss.Overline3()}
  color: ${cbSemanticCssVars.textSecondary};
`;
const DefaultEmptySectionContent = styled.div`
  color: ${cbSemanticCssVars.textTertiary};
  ${typographyCss.Body3()};
`;
const Column = styled.td`
  box-sizing: border-box;

  ${typographyCss.Body3()}

  text-align: ${({ $align }) => logicalTextAlign[$align]};
  vertical-align: ${({ $alignVertical }) => $alignVertical};
`;
const SectionColumn = styled.td``;
const SelectionColumn = styled(Column)`
  width: 40px;
`;
const Footer = styled.footer`
  box-sizing: border-box;
  min-height: 52px;
  border-top: 1px solid ${colorCssVars.borderColor};
  padding: 12px ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;

  overflow-x: auto;
`;
const PaginationContainer = styled.div`
  padding: 0 ${({ $horizontalPadding }) => horizontalPaddingConfig[$horizontalPadding]}px;

  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  grid-template-areas: 'empty pagination pageSizeSelector';
  grid-column-gap: 12px;
`;
const StyledPagination = styled(Pagination)`
  max-width: 100%;
  grid-area: pagination;
  justify-self: center;
  align-self: center;
`;
const StyledPageSizeSelector = styled(PageSizeSelector)`
  grid-area: pageSizeSelector;
  justify-self: end;
  align-self: center;
`;
const EmptyIconContainer = styled.div`
  ${createThemedCssVarDefinitions({
  background: { light: colors.pepper8, dark: colors.salt8 },
  iconFill: { light: colors.pepper40, dark: colors.salt48 }
})}
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background-color: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 44%;
    fill: var(--iconFill);
  }
`;
function DataTable({
  className,
  columns,
  componentProps,
  dataSource,
  elevation = 0,
  emptyMessage = { title: "No data" },
  footer,
  getRowKey,
  header,
  highlightSelectedRow,
  highlightUnclickableRowOnHover,
  horizontalPadding = "narrow",
  isRowClickable,
  loading,
  onColumnSortChange,
  onRowClick,
  pagination,
  renderSpinnerOnTop = false,
  roundedCorners = false,
  rowSelection,
  sectionsConfig,
  sortedColumn,
  style,
  tableLayout = "auto",
  verticalColumnBorders = false,
  verticalSpacing = "spacious"
}) {
  const allRowKeys = useMemo(() => dataSource.map(getRowKey), [dataSource, getRowKey]);
  const [activeKeyPressedRowKey, setKeyPressedRowKey] = useState();
  const hasFooterElement = !!footer || !!pagination;
  const recordSectionMap = useMemo(
    () => sectionsConfig ? dataSource.reduce((acc, record) => {
      const sectionId = sectionsConfig.getRowSectionId(record);
      if (acc[sectionId]) {
        acc[sectionId] = [...acc[sectionId], record];
      } else {
        acc[sectionId] = [record];
      }
      return acc;
    }, {}) : void 0,
    [sectionsConfig, dataSource]
  );
  const sections = useMemo(() => {
    if (!sectionsConfig) {
      return [{ id: void 0, name: "", render: void 0, records: dataSource }];
    }
    const sectionIds = sectionsConfig.sections.map((conf) => conf.id);
    return sectionIds.map((sectionId) => {
      const conf = sectionsConfig.sections.find((s) => s.id === sectionId);
      return {
        id: sectionId,
        name: (conf == null ? void 0 : conf.name) || sectionId,
        render: conf == null ? void 0 : conf.render,
        records: recordSectionMap && recordSectionMap[sectionId] ? recordSectionMap[sectionId] : []
      };
    });
  }, [recordSectionMap, sectionsConfig, dataSource]);
  const renderHeaderColumns = () => {
    let skipColumnsCount = 0;
    return columns.map(
      ({
        alignName = "left",
        className: columnClassName,
        id,
        info,
        name,
        nameColSpan,
        renderName = true,
        sortDirections = [],
        style: columnStyle
      }) => {
        if (skipColumnsCount > 0) {
          skipColumnsCount = skipColumnsCount - 1;
          return null;
        }
        if (nameColSpan && nameColSpan > 1) {
          skipColumnsCount = nameColSpan - 1;
        }
        return /* @__PURE__ */ jsx(
          HeaderColumn,
          {
            align: alignName,
            className: columnClassName,
            colSpan: nameColSpan,
            info,
            onSortChange: onColumnSortChange ? (direction) => {
              onColumnSortChange(id, direction);
            } : void 0,
            sortDirection: (sortedColumn == null ? void 0 : sortedColumn.id) === id ? sortedColumn.direction : void 0,
            sortDirections,
            style: columnStyle,
            children: renderName && name
          },
          id
        );
      }
    );
  };
  return /* @__PURE__ */ jsxs(Root, { $elevationLevel: elevation, $rounded: roundedCorners, className, style, children: [
    !!header && /* @__PURE__ */ jsx(Header, { $horizontalPadding: horizontalPadding, $rounded: roundedCorners, children: header }),
    /* @__PURE__ */ jsxs(
      Content,
      {
        $horizontalPadding: horizontalPadding,
        $verticalColumnBorders: verticalColumnBorders,
        children: [
          /* @__PURE__ */ jsxs(Table, { $rounded: roundedCorners && !header, $tableLayout: tableLayout, children: [
            /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(
              HeaderRow,
              {
                $horizontalPadding: horizontalPadding,
                $rounded: roundedCorners && !header,
                $verticalColumnBorders: verticalColumnBorders,
                $verticalSpacing: verticalSpacing,
                children: [
                  !!rowSelection && /* @__PURE__ */ jsx(HeaderSelectionColumn, { align: "left", children: /* @__PURE__ */ jsx(
                    InputCheckbox,
                    {
                      checked: dataSource.length > 0 && allRowKeys.every((key) => rowSelection.selectedRowKeys.includes(key)),
                      onChange: () => {
                        if (rowSelection.onSelectAll) {
                          rowSelection.onSelectAll();
                        }
                      },
                      partiallyChecked: allRowKeys.some(
                        (key) => rowSelection.selectedRowKeys.includes(key)
                      )
                    }
                  ) }),
                  renderHeaderColumns()
                ]
              }
            ) }),
            (renderSpinnerOnTop || !loading) && /* @__PURE__ */ jsx(
              Body,
              {
                $horizontalPadding: horizontalPadding,
                $rounded: roundedCorners && !hasFooterElement,
                $verticalSpacing: verticalSpacing,
                children: sections.map(
                  ({
                    id: sectionId,
                    name: sectionName,
                    records,
                    render: renderSection = ({ sectionName: sectionName2 }) => /* @__PURE__ */ jsx(SectionTitle, { children: sectionName2 })
                  }) => (records.length > 0 || (sectionsConfig == null ? void 0 : sectionsConfig.showEmptySections)) && /* @__PURE__ */ jsxs(React.Fragment, { children: [
                    !!sectionId && /* @__PURE__ */ jsx(
                      "tr",
                      {
                        title: sectionName === void 0 ? void 0 : `Section ${sectionName}`,
                        children: /* @__PURE__ */ jsx(SectionColumn, { colSpan: columns.length, children: renderSection({
                          ...componentProps,
                          records,
                          sectionName
                        }) })
                      }
                    ),
                    records.length === 0 ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx(EmptySectionContentColumn, { colSpan: columns.length, children: !!sectionsConfig && !!sectionsConfig.renderEmptySectionContent ? sectionsConfig.renderEmptySectionContent({
                      ...componentProps,
                      sectionName
                    }) : /* @__PURE__ */ jsx(DefaultEmptySectionContent, { children: "No data in this section" }) }) }) : records.map((record, rowIndex) => {
                      var _a;
                      const clickDisabled = !!onRowClick && !!isRowClickable && !isRowClickable(record);
                      const rowKey = getRowKey(record);
                      const selected = (_a = rowSelection == null ? void 0 : rowSelection.selectedRowKeys) == null ? void 0 : _a.includes(rowKey);
                      return /* @__PURE__ */ jsxs(
                        Row,
                        {
                          $active: activeKeyPressedRowKey === rowKey,
                          $clickDisabled: clickDisabled,
                          $hasClickAssigned: !!onRowClick,
                          $highlightSelectedRow: Boolean(highlightSelectedRow),
                          $highlightUnclickableRowOnHover: Boolean(
                            highlightUnclickableRowOnHover
                          ),
                          $horizontalPadding: horizontalPadding,
                          $selected: Boolean(selected),
                          $verticalColumnBorders: verticalColumnBorders,
                          $verticalSpacing: verticalSpacing,
                          "aria-disabled": clickDisabled,
                          onClick: onRowClick && !clickDisabled ? (event) => onRowClick(rowKey, record, event) : void 0,
                          onKeyDown: onRowClick && !clickDisabled ? (event) => {
                            if (event.key === " ") {
                              setKeyPressedRowKey(rowKey);
                              onRowClick(rowKey, record, event);
                            }
                          } : void 0,
                          onKeyUp: () => setKeyPressedRowKey(void 0),
                          role: onRowClick ? "button" : void 0,
                          tabIndex: onRowClick ? 0 : void 0,
                          children: [
                            !!rowSelection && /* @__PURE__ */ jsx(SelectionColumn, { $align: "left", children: /* @__PURE__ */ jsx(
                              InputCheckbox,
                              {
                                checked: selected,
                                onChange: () => {
                                  const newKeys = rowSelection.selectedRowKeys.includes(rowKey) ? rowSelection.selectedRowKeys.filter(
                                    (key) => key !== rowKey
                                  ) : [...rowSelection.selectedRowKeys, rowKey];
                                  const records2 = dataSource.filter(
                                    (d) => newKeys.includes(getRowKey(d))
                                  );
                                  rowSelection.onChange(newKeys, records2);
                                },
                                onClick: (event) => event.stopPropagation()
                              }
                            ) }),
                            columns.map(
                              ({
                                alignContent = "left",
                                alignContentVertical = "middle",
                                id,
                                render
                              }) => /* @__PURE__ */ jsx(
                                Column,
                                {
                                  $align: alignContent,
                                  $alignVertical: alignContentVertical,
                                  children: render({
                                    ...componentProps,
                                    record,
                                    rowIndex
                                  })
                                },
                                id
                              )
                            )
                          ]
                        },
                        rowKey
                      );
                    })
                  ] }, sectionId || "no-id")
                )
              }
            )
          ] }),
          !(sectionsConfig == null ? void 0 : sectionsConfig.showEmptySections) && (!loading || renderSpinnerOnTop) && dataSource.length === 0 && /* @__PURE__ */ jsx(EmptyContainer, { children: /* @__PURE__ */ jsx(
            Empty,
            {
              description: emptyMessage.description,
              footer: emptyMessage.footer,
              image: (emptyMessage == null ? void 0 : emptyMessage.icon) ? /* @__PURE__ */ jsx(EmptyIconContainer, { children: emptyMessage == null ? void 0 : emptyMessage.icon }) : void 0,
              title: emptyMessage.title
            }
          ) }),
          !!loading && !renderSpinnerOnTop && /* @__PURE__ */ jsx(SpinnerContainer, { children: /* @__PURE__ */ jsx(Spinner, { size: "large" }) }),
          !!loading && !!renderSpinnerOnTop && /* @__PURE__ */ jsx(
            SpinnerOverlayContainer,
            {
              $elevationLevel: elevation,
              $offset: verticalSpacing === "compact" ? 36 : 56,
              $rounded: !footer && !pagination && !!roundedCorners,
              children: /* @__PURE__ */ jsx(Spinner, { size: "large" })
            }
          )
        ]
      }
    ),
    !!footer && !pagination && /* @__PURE__ */ jsx(Footer, { $horizontalPadding: horizontalPadding, children: footer }),
    !!pagination && /* @__PURE__ */ jsx(Footer, { $horizontalPadding: "none", children: /* @__PURE__ */ jsxs(PaginationContainer, { $horizontalPadding: horizontalPadding, children: [
      /* @__PURE__ */ jsx(
        StyledPagination,
        {
          activePage: pagination.activePage,
          disabled: pagination.disableWhenLoading && loading,
          onPageChange: pagination.onPageChange,
          pages: pagination.pageCount
        }
      ),
      pagination.pageSize !== void 0 && /* @__PURE__ */ jsx(
        StyledPageSizeSelector,
        {
          onChange: pagination.onPageSizeChange,
          options: pagination.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS,
          value: pagination.pageSize
        }
      )
    ] }) })
  ] });
}
export {
  DataTable,
  Pagination
};
//# sourceMappingURL=index.js.map
